// components/Teams/TeamFormModal.js
import React, { useState, useEffect } from 'react';

const TeamFormModal = ({ 
  show, 
  onClose, 
  onSubmit, 
  loading, 
  teamToEdit,
  mode = 'create' // 'create' or 'edit'
}) => {
  const [formData, setFormData] = useState({
    team_name: '',
    description: '',
    team_type: 'office',
    custom_type: ''
  });

  // Team type options with icons/labels
  const teamTypes = [
    { value: 'office', label: '🏢 Office', description: 'General office team' },
    { value: 'candidate', label: '👥 Candidate', description: 'Candidate management team' },
    { value: 'military', label: '🎖️ Military', description: 'Military operations team' },
    { value: 'security', label: '🛡️ Security', description: 'Security team' },
    { value: 'operations', label: '⚙️ Operations', description: 'Operations team' },
    { value: 'research', label: '🔬 Research', description: 'Research and development' },
    { value: 'custom', label: '✨ Custom', description: 'Create your own team type' }
  ];

  useEffect(() => {
    if (teamToEdit) {
      setFormData({
        team_name: teamToEdit.team_name || '',
        description: teamToEdit.description || '',
        team_type: teamToEdit.team_type || 'office',
        custom_type: teamToEdit.custom_type || ''
      });
    } else {
      setFormData({
        team_name: '',
        description: '',
        team_type: 'office',
        custom_type: ''
      });
    }
  }, [teamToEdit]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate custom_type when team_type is 'custom'
    if (formData.team_type === 'custom' && !formData.custom_type.trim()) {
      alert('Please enter a custom team type name');
      return;
    }
    
    onSubmit(formData);
    if (mode === 'create') {
      // Only reset form after create, keep data for edit in case of error
      setFormData({
        team_name: '',
        description: '',
        team_type: 'office',
        custom_type: ''
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!show) return null;

  return (
    <div className="teams-modal-overlay">
      <div className="teams-modal">
        <div className="teams-modal-header">
          <h2>{mode === 'edit' ? 'Edit Team' : 'Create New Team'}</h2>
          <button 
            className="teams-modal-close"
            onClick={onClose}
            type="button"
          >
            &times;
          </button>
        </div>
        <form onSubmit={handleSubmit} className="teams-modal-form">
          <div className="form-group">
            <label htmlFor="team_name">Team Name *</label>
            <input
              type="text"
              id="team_name"
              name="team_name"
              value={formData.team_name}
              onChange={handleChange}
              placeholder="Enter team name"
              required
              maxLength="100"
            />
          </div>

          <div className="form-group">
            <label htmlFor="team_type">Team Type *</label>
            <select
              id="team_type"
              name="team_type"
              value={formData.team_type}
              onChange={handleChange}
              className="team-type-select"
              required
            >
              {teamTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <small className="form-text">
              {teamTypes.find(t => t.value === formData.team_type)?.description}
            </small>
          </div>

          {/* Custom Type Input - Shows only when 'custom' is selected */}
          {formData.team_type === 'custom' && (
            <div className="form-group custom-type-group">
              <label htmlFor="custom_type">Custom Team Type Name *</label>
              <input
                type="text"
                id="custom_type"
                name="custom_type"
                value={formData.custom_type}
                onChange={handleChange}
                placeholder="Enter custom team type (e.g., Special Forces, R&D Lab)"
                required
                maxLength="50"
                className="custom-type-input"
                autoFocus
              />
              <small className="form-text">
                Enter a name for your custom team type
              </small>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter team description (optional)"
              rows="4"
              maxLength="500"
            />
          </div>

          <div className="teams-modal-actions">
            <button 
              type="button"
              className="cancel-btn"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="submit-btn"
              disabled={loading}
            >
              {loading ? (mode === 'edit' ? 'Updating...' : 'Creating...') : (mode === 'edit' ? 'Update Team' : 'Create Team')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeamFormModal;